import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import type { MouseEvent, ReactElement } from 'react';
import { ListIcon } from 'assets/icons/v2';
import { TextLink } from 'components/ui/TextLink';
import type { ButtonRecipeProps } from 'styles/theme/recipes/button.recipe';
import { ProtectedRoutes } from 'types/Routes';

const FIGMA_BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'secondaryOutline',
  'tertiary',
  'tertiaryAction',
  'destructive',
  'destructiveOutline',
] as const satisfies ReadonlyArray<NonNullable<ButtonRecipeProps['variant']>>;

const VARIANT_LABEL: Record<(typeof FIGMA_BUTTON_VARIANTS)[number], string> = {
  primary: 'Speichern',
  secondary: 'Abbrechen',
  secondaryOutline: 'Zurück',
  tertiary: 'Details',
  tertiaryAction: 'Mehr erfahren',
  destructive: 'Löschen',
  destructiveOutline: 'Entfernen',
};

const SIZES = ['l', 'm'] as const;

const noopLinkClick = (event: MouseEvent<HTMLAnchorElement>): void => {
  event.preventDefault();
};

/**
 * Matrix of UI-kit buttons and text links for visual QA next to Figma.
 */
export const ButtonTextLinkPreview = (): ReactElement => {
  return (
    <VStack gap="xl" align="stretch" w="full">
      <VStack gap="xs" align="stretch">
        <Text textStyle="h3.medium" color="text.primary">
          Buttons & TextLinks (UI-Kit)
        </Text>
        <Text textStyle="body.s.regular" color="text.tertiary">
          Vorschau für Rezept-Varianten, Größen und Zustände. Die TextLinks unten lösen keine
          Navigation aus (Klick wird unterdrückt).
        </Text>
      </VStack>

      <Box
        bg="background.canvas"
        p="xl"
        borderRadius="l"
        borderWidth="1px"
        borderColor="border.accent"
      >
        <VStack gap="2xl" align="stretch">
          <Text textStyle="h4.medium" color="text.primary">
            Button
          </Text>

          {FIGMA_BUTTON_VARIANTS.map((variant) => (
            <VStack key={variant} gap="l" align="stretch">
              <Text textStyle="body.m.medium" color="text.secondary">
                {variant}
              </Text>

              {SIZES.map((size) => (
                <VStack key={`${variant}-${size}`} gap="xs" align="stretch">
                  <HStack flexWrap="wrap" gap="m" align="center">
                    <Button type="button" variant={variant} size={size}>
                      {VARIANT_LABEL[variant]}
                    </Button>
                    <Button type="button" variant={variant} size={size} disabled>
                      {VARIANT_LABEL[variant]}
                    </Button>
                    <Button
                      type="button"
                      variant={variant}
                      size={size}
                      loading
                      loadingText="Wird ausgeführt…"
                    >
                      {VARIANT_LABEL[variant]}
                    </Button>
                  </HStack>
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    {`variant="${variant}" size="${size}"`}
                  </Text>
                </VStack>
              ))}

              <VStack gap="xs" align="stretch">
                <HStack flexWrap="wrap" gap="m" align="center">
                  <Button type="button" variant={variant}>
                    <ListIcon boxSize="16px" />
                    Mit Symbol
                  </Button>
                  <Button type="button" variant={variant}>
                    Symbol rechts
                    <ListIcon boxSize="16px" />
                  </Button>
                  <Button type="button" variant={variant} size="m">
                    <ListIcon boxSize="16px" />
                    Mit Symbol
                  </Button>
                  <Button type="button" variant={variant} size="m">
                    Symbol rechts
                    <ListIcon boxSize="16px" />
                  </Button>
                </HStack>
                <Text textStyle="body.xs.regular" color="text.tertiary">
                  {`variant="${variant}" size="m / l" leftIcon / rightIcon`}
                </Text>
              </VStack>
            </VStack>
          ))}
        </VStack>
      </Box>

      <Box
        bg="background.canvas"
        p="xl"
        borderRadius="l"
        borderWidth="1px"
        borderColor="border.accent"
      >
        <VStack gap="l" align="stretch">
          <Text textStyle="h4.medium" color="text.primary">
            TextLink
          </Text>
          <Text textStyle="body.s.regular" color="text.tertiary">
            <strong>linkScope=&quot;external&quot;</strong> nutzt Rezept <strong>info</strong>{' '}
            (Unterstreichung); <strong>linkScope=&quot;internal&quot;</strong> standardmäßig{' '}
            <strong>inline</strong> (gestrichelt).
          </Text>

          {(['external', 'internal'] as const).map((linkScope) => (
            <VStack key={linkScope} gap="m" align="stretch">
              <Text textStyle="body.m.medium" color="text.secondary">
                linkScope=&quot;{linkScope}&quot;
              </Text>
              {SIZES.map((size) => (
                <VStack key={`${linkScope}-${size}`} gap="xs" align="stretch">
                  {linkScope === 'external' ? (
                    <TextLink
                      linkScope="external"
                      size={size}
                      href="https://example.com"
                      onClick={noopLinkClick}
                    >
                      Externer Link
                    </TextLink>
                  ) : (
                    <TextLink
                      linkScope="internal"
                      size={size}
                      to={ProtectedRoutes.HOME}
                      onClick={noopLinkClick}
                    >
                      Interner Link
                    </TextLink>
                  )}
                  <Text textStyle="body.xs.regular" color="text.tertiary">
                    {`linkScope="${linkScope}" size="${size}"`}
                  </Text>
                </VStack>
              ))}
            </VStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};
